import dayjs from "dayjs";
import { DAYS_OF_WEEK_DB } from "../constants";
import { parseDate, parseTime, sortTimeFrames, timeIsInRange } from "../date";
import { FormattedAmenityData, MergedAvailability, PrecomputedAmenityData } from "~/types";
import * as APITypes from "~/API";

export const enum AmenityStatus {
  Available = "Available",
  Reserved = "Reserved",
  Unreservable = "Unreservable",
  Unavailable = "Unavailable",
  Open = "Open",
  Closed = "Closed",
  Unspecified = "Unspecified",
}

export const mergeAvailability = (amenity: FormattedAmenityData): MergedAvailability => {
  let merged: MergedAvailability = {
    available: [[], [], [], [], [], [], []],
    unavailable: [],
    fullyUnavailable: false,
  };

  const availabilityStrings = amenity.amenityDetails.availability;

  if (!availabilityStrings || availabilityStrings.length === 0) {
    merged.fullyUnavailable = true;
    return merged;
  }

  let daysOfWeek = [
    DAYS_OF_WEEK_DB.sunday,
    DAYS_OF_WEEK_DB.monday,
    DAYS_OF_WEEK_DB.tuesday,
    DAYS_OF_WEEK_DB.wednesday,
    DAYS_OF_WEEK_DB.thursday,
    DAYS_OF_WEEK_DB.friday,
    DAYS_OF_WEEK_DB.saturday,
  ];

  // Parsing the data in the form
  // Typical availability:
  //   (DaysOfWeek)-(TimeStart)-(TimeEnd)
  // Not available for certain time, given reason:
  //   N-(DateStart)-(TimeStart)-(DateEnd)-(TimeEnd)-(Reason)
  // Not availabile for the forseeable future, given reason:
  //   N-(Reason)
  // Reserved for the given time frame:
  //   E-(DateStart)-(TimeStart)-(DateEnd)-(TimeEnd)
  for (let i = 0; i < availabilityStrings.length; i++) {
    const s = availabilityStrings[i] || "";
    const split = s.split("-");
    if (split[0] === "N") {
      if (split.length === 2) {
        merged.fullyUnavailable = split[1];
      } else {
        const start = parseDate(split[1], split[2]);
        const end = parseDate(split[3], split[4]);
        const reason = split[5];
        merged.unavailable.push({
          timeFrame: {start, end},
          reason,
        });
      }
    } else {
      const start = parseTime(split[1]);
      const end = parseTime(split[2]);

      for (let day = 0; day < 7; day++) {
        if (s.includes(daysOfWeek[day])) {
          merged.available[day].push({start, end});
        }
      }
    }
  }

  // Sort times by start
  for (let day = 0; day < 7; day++) {
    sortTimeFrames(merged.available[day], "minute");
  }

  // Merge times
  for (let day = 0; day < 7; day++) {
    let last = 0;
    for (let current = 1; current < merged.available[day].length; current++) {
      /* if the last end time is after (or the same as) the start time of the current, merge
       * i.e. |--------------|-------------|...
       *  last start   current start   last end
       */
      if (
        merged.available[day][last].end.isAfter(merged.available[day][current].start) ||
        merged.available[day][last].end.isSame(merged.available[day][current].start)
      ) {
        /* if the last end time is also after (or the same as) the end time of the current,
         * the previous time encompasses the current so it can be ignored/removed.
         * i.e. |---------------|--------------|------------|
         *  last start    current start   current end   last end
         */
        if (
          merged.available[day][last].end.isAfter(merged.available[day][current].end) ||
          merged.available[day][last].end.isSame(merged.available[day][current].end)
        ) {
          continue; // will be removed in bulk later
        }
        /* otherwise, update the end time of last
         *      |---------------|--------------|------------|
         *  last start    current start   last end   current end
         * becomes merged:
         *      |-------------------------------------------|
         *  last start                                  last end
         */
        merged.available[day][last].end = merged.available[day][current].end;
        continue; // will be removed in bulk later
      }
      /* if the last end time is before the start time of the current,
       * set last to current, remove all previous time frames up to last
       * (they have all been merged into last)
       * i.e. |------------|-------------|...
       *  last start   last end   current start
       */
      const toRemove = current - last - 1;
      merged.available[day].splice(last + 1, toRemove);
      current -= toRemove;
      last = current;
    }
    const current = merged.available[day].length - 1;
    if (last < current) {
      const toRemove = current - last;
      merged.available[day].splice(last + 1, toRemove);
    }
  }

  return merged;
};

export const getAmenityStatus = (amenityData: PrecomputedAmenityData): [AmenityStatus, string | undefined] => {
  const reservations = amenityData.formattedAmenityDetails.reservations;
  const availability = amenityData.availability;
  const currentTime = dayjs();

  if (availability.fullyUnavailable !== false) {
    return (availability.fullyUnavailable === true)
      ? [AmenityStatus.Unspecified, undefined]
      : [AmenityStatus.Unavailable, availability.fullyUnavailable];
  }
  for (let i = 0; i < availability.unavailable.length; i++) {
    const unavailable = availability.unavailable[i].timeFrame;
    if (timeIsInRange(unavailable, currentTime))
      return [AmenityStatus.Unavailable, availability.unavailable[i].reason];
  }
  for (let i = 0; i < reservations.length; i++) {
    if (timeIsInRange(reservations[i].timeFrame, currentTime))
      return [AmenityStatus.Reserved, undefined];
  }
  const reservationPolicy = amenityData.formattedAmenityDetails.amenityDetails.reservationRule.policy;
  const reservationRequired = reservationPolicy === APITypes.AmenityReservationPolicyOptions.Required;
  const todaysAvail = availability.available[currentTime.day()];
  if (!todaysAvail) {
    return reservationRequired
      ? [AmenityStatus.Unreservable, undefined]
      : [AmenityStatus.Closed, undefined];
  }
  for (let i = 0; i < todaysAvail.length; i++) {
    if (timeIsInRange(todaysAvail[i], currentTime))
      return reservationRequired ? [AmenityStatus.Available, undefined] : [AmenityStatus.Open, undefined];
  }
  return reservationRequired ? [AmenityStatus.Unreservable, undefined] : [AmenityStatus.Closed, undefined];
}